import { useMemo } from "react";
import { Card, Title, Subtitle, BarChart, DonutChart, Legend } from "@tremor/react";
import { CalendarRange, MousePointerClick } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { deriveMix } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { useDrilldown } from "@/components/drilldown/Drilldown";
import { labelCategorie } from "@/lib/labels";
import { formatLunaKey, eurAxis, eur } from "@/lib/format";

const PALETTE = ["blue", "emerald", "amber", "violet", "rose", "cyan", "indigo"] as const;

export function MixSezonalitateSection() {
  useFilterVersion();
  const { openDrill } = useDrilldown();
  const mix = deriveMix();

  const serie = useMemo(
    () =>
      mix.serie.map((row) => {
        const out: Record<string, number | string> = { luna: formatLunaKey(String(row.luna)) };
        for (const cat of mix.categorii) out[labelCategorie(cat)] = row[cat] as number;
        return out;
      }),
    [mix],
  );

  const categoriiLabel = mix.categorii.map(labelCategorie);
  const colors = mix.categorii.map((_, i) => PALETTE[i % PALETTE.length]);
  const donut = mix.totalPerCategorie.map((t) => ({
    categorie: labelCategorie(t.categorie),
    cheie: t.categorie,
    venit_eur: t.venit_eur,
  }));

  return (
    <section>
      <SectionHeader
        id="mix-sezonalitate"
        icon={CalendarRange}
        titlu="7. Mix produse & sezonalitate"
        descriere="Distribuția venitului pe categorii, lună de lună — ce se vinde, când, și cum variază sezonier."
      />

      <div className="mb-3 flex items-center gap-1.5 text-xs text-slate-400">
        <MousePointerClick className="h-3.5 w-3.5" />
        Click pe un segment din donut → comenzile categoriei.
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="ring-slate-200 lg:col-span-2">
          <Title>Venit pe categorii, în timp</Title>
          <Subtitle>Coloane stivuite — vârfuri sezoniere pe perioada filtrată</Subtitle>
          <BarChart
            className="mt-4 h-80 w-full"
            data={serie}
            index="luna"
            categories={categoriiLabel}
            colors={colors}
            stack
            valueFormatter={eurAxis}
            yAxisWidth={56}
            noDataText="Fără date pe filtrul curent"
          />
        </Card>

        <Card className="flex flex-col ring-slate-200">
          <Title>Distribuție pe categorii</Title>
          <Subtitle>Pondere din venit total</Subtitle>
          <DonutChart
            className="mt-4 h-52 w-full"
            data={donut}
            category="venit_eur"
            index="categorie"
            colors={colors}
            valueFormatter={(v) => eur(v, 0)}
            noDataText="Fără date"
            onValueChange={(v) => {
              const cheie = (v as { cheie?: string } | null)?.cheie;
              if (cheie) openDrill({ type: "categorie", valoare: cheie });
            }}
          />
          <Legend className="mt-3 justify-center" categories={categoriiLabel} colors={colors} />
        </Card>
      </div>
    </section>
  );
}
